/**
 * PlanPage — rendered at /app/project/:projectId/plan (nested under
 * ProjectDataLoader, which fetches and hydrates the project before this
 * component ever mounts — see src/hooks/useProjectData.ts).
 *
 * Built on the shared AppPage layout (see ./AppPage). The body is a single
 * main container, sized to match the combined width of the three panels on
 * InsightsPage (each 30% wide, capped at 460px, with 20px gaps — see
 * ./InsightsPage). Inside it:
 *  - a left column, its full height taken up by the "Plan" panel — a list of
 *    draggable PlanBoardCards (every story/experiment across the project's
 *    trees), built on PlanBoardColumn.tsx / PlanBoardCard.tsx
 *  - a right-hand column filling the remaining width, its full height taken up
 *    by the "Timeline" panel (a pan/zoom grid — see canvas/TimelineCanvas.tsx);
 *    it used to share this column with a "Details" panel below it, since removed
 *
 * This page owns the drag state machine that moves PlanBoardCards between
 * the Plan column and the Timeline grid. During a drag, the dragged card is
 * hidden and replaced by a plain translucent/dashed placeholder box (the
 * "ghost") rather than a lookalike copy of the card — this sidesteps the
 * text-rasterization/font-size flicker that came with animating a card's
 * content between its list and canvas layouts. The ghost's POSITION always
 * instantly tracks the cursor (or the snapped cell/slot once it's over a
 * drop region) — never animated, so it never lags behind the pointer. Its
 * SIZE, on the other hand, targets whichever region it's currently over and
 * is continuously CSS-transitioned toward that target — since the target
 * value only actually changes when the cursor crosses into a new region or
 * grid cell (not on every pixel of movement), this animates smoothly exactly
 * once per crossing, live, while still dragging. On release, finishDrag
 * computes the authoritative destination and — for a cross-context drop —
 * keeps the ghost around briefly (beginSettle) so any still-in-flight resize
 * finishes before handing off to the real element. A card is only "picked
 * up" (hidden from its origin, replaced by the floating ghost) once the
 * mouse has moved past a small threshold — until then it just shows an
 * elevated shadow in place (see pressedItemId), so a plain click never
 * jitters its contents.
 *
 * The one drag shape this shares with the simple single-list panels elsewhere in the app
 * (InsightsPanel/TriagePanel on the OST canvas, the standalone InsightsPage) is a same-list
 * reorder — a card that started in the Plan column and hasn't left it. That case reuses those
 * panels' own live "slide the other cards out of the way" preview, built on the shared
 * utils/dragSortList.ts pure functions and CSS classes (see isSameListReorder/planDragSortState
 * and PlanBoardColumn's own dragSortState prop below) — everything else here (the canvas
 * hand-off, the resizing ghost, the placeholder-gap preview for a card arriving FROM the canvas)
 * has no equivalent in those panels and stays this page's own.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import AppPage from './AppPage';
import AppPagePanel from 'components/ui/AppPagePanel';
import PlanBoardColumn, { PLAN_BOARD_COLUMN_PLACEHOLDER_HEIGHT } from './PlanBoardColumn';
import PlanBoardCard from './PlanBoardCard';
import TimelineCanvas, {
    TimelineCanvasHandle,
    ScreenRect,
    ResizeEdge,
    CanvasConnector,
    CanvasConnectorPreview,
    PLAN_CARD_DEFAULT_WIDTH,
    PLAN_CARD_DEFAULT_HEIGHT,
    TIMELINE_FROZEN_COLUMN_WIDTH,
    TIMELINE_FROZEN_ROW_HEIGHT,
    TIMELINE_DEFAULT_LINE_COLOR,
} from 'components/canvas/TimelineCanvas';
import usePlanBoardItems from 'hooks/usePlanBoardItems';
import usePlanSync from 'hooks/usePlanSync';
import useConnectDrag from 'hooks/useConnectDrag';
import useModifierKeyActive from 'hooks/useModifierKeyActive';
import { useStore } from 'store/useStore';
import { savePlanBoard } from 'api/projects';
import { isErrorResponse } from 'utils/apiUtils';
import { randomId } from 'utils/ostUtils';
import { upsertAxisLabel, clearAxisLabelsForOrientation } from 'utils/planAxisLabels';
import { COLORS } from 'utils/canvasUtils';
import { getDragSortTargetIndex } from 'utils/dragSortList';
import { PLAN_CONNECTION_RELATIONSHIP_META } from 'utils/planConnectionRelationships';
import PlanConnectionRelationshipPopover from './PlanConnectionRelationshipPopover';
import PlanSyncButton from './PlanSyncButton';
import PlanTimelineToolbar, { PlanToolbarAction } from './PlanTimelineToolbar';
import SyncResultModal from 'components/modals/SyncResultModal';
import AddEnablerCardModal from 'components/modals/AddEnablerCardModal';
import { PlanBoardItem, EnablerCardItem, PlanBoardConnection, PlanCardPlacement, PlanDrawnLine, PlanLineOrientation, PlanLineCategory, PlanAxisLabel, PlanConnectionRelationshipType } from 'types';

const MAIN_CONTAINER_STYLE: React.CSSProperties = {
    width: '90%',
    maxWidth: 1420,
    display: 'flex',
    gap: '20px',
    flex: 1,
    minHeight: 0,
};

const RIGHT_COLUMN_STYLE: React.CSSProperties = {
    flex: '1 1 70%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    minHeight: 0,
};

const LEFT_COLUMN_STYLE: React.CSSProperties = {
    flex: '0 0 20%',
    width: '20%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    minHeight: 0,
};

// Icon-only glyphs for the Timeline canvas's corner toolbar — plain stroked SVGs matching
// ProjectMenu's inline-icon style (currentColor stroke, no fill) rather than a text label.
const TIMELINE_MENU_VERTICAL_LINE_ICON = (
    <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M8 1.5V14.5' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
    </svg>
);

const TIMELINE_MENU_HORIZONTAL_LINE_ICON = (
    <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M1.5 8H14.5' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
    </svg>
);

/** "Add enabler card" toolbar button — a plain plus, matching the other two icons' stroked,
 * currentColor-only style rather than a filled glyph. */
const TIMELINE_MENU_ADD_ENABLER_ICON = (
    <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M8 1.5V14.5M1.5 8H14.5' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
    </svg>
);

/** Preset swatches offered in the line color picker popover — orange first (matching
 * TIMELINE_DEFAULT_LINE_COLOR, the default a new line is drawn with) plus a handful of other
 * hues that read clearly against the blueprint-blue canvas and white grid/dividers. */
const LINE_COLOR_PRESETS = ['#f28c5c', '#f2d34e', '#e35d5d', '#63c17a', '#8f7cf2', '#f5f5f5'];

/** A new line's category is implied entirely by its orientation for now — vertical lines mark
 * priorities, horizontal lines mark releases — so there's no per-line category control to show;
 * see PlanLineCategory in types.ts for what these values mean elsewhere. */
function defaultCategoryForOrientation(orientation: PlanLineOrientation): PlanLineCategory {
    return orientation === 'vertical' ? 'priority' : 'release';
}

/** Arrow color for one PlanBoardConnection — the assigned relationship's color if it has one
 * (see PlanConnectionRelationshipPopover/PlanConnectionRelationshipMeta), otherwise
 * `fallbackColor` (today's per-source-item-type default — see connectorColorForItem below).
 * Exported as a standalone pure function, like this file's other small derivations above, so the
 * branch is unit-testable without mounting the page — see PlanPage.test.ts.
 *
 * `relationshipType` comes from persisted data (DB/localStorage), which TypeScript cannot
 * validate at runtime — a connection saved under a since-renamed or otherwise unrecognized type
 * string falls back to `fallbackColor` rather than crashing on a missing metadata entry. */
export function resolveConnectorColor(connection: Pick<PlanBoardConnection, 'relationshipType'>, fallbackColor: string): string {
    const meta = connection.relationshipType ? PLAN_CONNECTION_RELATIONSHIP_META[connection.relationshipType] : undefined;
    return meta ? meta.color : fallbackColor;
}

/** Filters out any connection touching `itemId` — used when a card is dragged from the canvas
 * back to its panel, which breaks its connections (see PlanBoardConnection's doc comment,
 * types.ts): a connector only means something between two currently placed cards, so unplacing
 * one end discards it rather than leaving it to silently reappear if the card is dragged back
 * onto the canvas later. Returns the input array unchanged (same reference) when nothing matched,
 * so callers can skip a state update. Exported as a standalone pure function, like this file's
 * other small derivations above, so the branch is unit-testable without mounting the page — see
 * PlanPage.test.ts. */
export function pruneConnectionsForUnplacedItem(connections: PlanBoardConnection[], itemId: string): PlanBoardConnection[] {
    const next = connections.filter((c) => c.fromItemId !== itemId && c.toItemId !== itemId);
    return next.length === connections.length ? connections : next;
}

/** CSS transition duration (ms) for the ghost's width/height/padding — live during drag AND at settle.
 * Kept short so the list↔canvas hand-off reads as near-instant rather than a visible lag. */
const RESIZE_TRANSITION_MS = 80;
/** Delay before a cross-context settle commits — must exceed RESIZE_TRANSITION_MS so the animation finishes first. */
const SETTLE_COMMIT_DELAY_MS = RESIZE_TRANSITION_MS + 20;
/** Pixels the pointer must move past mousedown before a press becomes an actual drag. */
const DRAG_THRESHOLD_PX = 4;
/**
 * Only the ghost root's own shape properties animate here — left/top are deliberately
 * excluded so position tracking is always instant. The ghost is a plain placeholder box
 * (no card content), so only width/height ever need to animate.
 */
const GHOST_RESIZE_TRANSITION = `width ${RESIZE_TRANSITION_MS}ms ease, height ${RESIZE_TRANSITION_MS}ms ease`;

type DragOriginKind = 'list' | 'canvas';

type ActiveDrag = {
    itemId: string;
    origin: DragOriginKind;
    pointerOffsetX: number;
    pointerOffsetY: number;
    ghostLeft: number;
    ghostTop: number;
    ghostWidth: number;
    ghostHeight: number;
    overCanvas: boolean;
    /** True when `overCanvas` is only true because the pointer is over the frozen row/column —
     * no card, whether dragged in from the Plan panel or repositioned on the canvas itself, can
     * land there (see resolveFrozenAreaCandidate). finishDrag uses this to treat the hover as
     * not a real canvas drop target: a list-origin drag falls back to the list-release path
     * (landing back at its original spot), and a canvas-origin drag is left untouched entirely
     * (its placement was never written to, so it simply stays where it was). */
    canvasDropRejected: boolean;
    overList: boolean;
    listInsertIndex: number | null;
    /** Measured height (px) of the dragged item as it would render in the list, for the drop placeholder. */
    listInsertHeight: number | null;
    /** Live snapped destination rect (canvas units) while hovering the Timeline grid. */
    canvasPreview: PlanCardPlacement | null;
};

type SettleAnim = {
    itemId: string;
    to: ScreenRect;
    onSettled: () => void;
};

export function hitTestRect(rect: DOMRect | null, x: number, y: number): boolean {
    if (!rect) return false;
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

type CanvasGhostPreview = {
    canvasPreview: PlanCardPlacement | null;
    /** True when the candidate placement was thrown out specifically because it's mostly over
     * the frozen row/column (see resolveFrozenAreaCandidate) — distinct from canvasPreview being
     * null because the canvas API couldn't resolve a point at all. */
    rejected: boolean;
    ghostLeft: number;
    ghostTop: number;
    ghostWidth: number;
    ghostHeight: number;
};

/**
 * No card can be dropped on the frozen row or column, whether it's arriving from the Plan panel
 * or being repositioned from elsewhere on the canvas — they're reserved chrome, not a placement
 * lane. Each axis is judged independently by how much of the candidate's extent on that axis
 * overlaps its frozen band:
 *  - more than half overlapping either band: rejected outright (returns null) — see
 *    finishDrag/canvasDropRejected for what happens to the drag in that case.
 *  - half or less: nudged forward past that band's edge (left edge past the frozen column,
 *    and/or top edge past the frozen row) so it lands fully in the pannable area instead of
 *    being clipped by it.
 *  - no overlap on an axis at all: that axis is left unchanged.
 *
 * `frozenColumnWidth`/`frozenRowHeight` default to the fully-expanded canvas-space constants but
 * are meant to be called with TimelineCanvasHandle's getFrozenColumnWidth()/getFrozenRowHeight()
 * (see computeCanvasGhostPreview below) — those track the live hover-collapse animation, so a
 * pane that's been collapsed toward its small persistent strip shrinks the reserved/no-drop band
 * right along with what's actually visible on screen, instead of leaving it pinned to the
 * fully-expanded size regardless of collapse state.
 */
export function resolveFrozenAreaCandidate(
    candidate: PlanCardPlacement,
    frozenColumnWidth: number = TIMELINE_FROZEN_COLUMN_WIDTH,
    frozenRowHeight: number = TIMELINE_FROZEN_ROW_HEIGHT,
): PlanCardPlacement | null {
    const columnOverlap = Math.max(0, Math.min(candidate.x + candidate.width, frozenColumnWidth) - Math.max(candidate.x, 0));
    const rowOverlap = Math.max(0, Math.min(candidate.y + candidate.height, frozenRowHeight) - Math.max(candidate.y, 0));
    if (columnOverlap / candidate.width > 0.5) return null;
    if (rowOverlap / candidate.height > 0.5) return null;

    return {
        ...candidate,
        x: columnOverlap > 0 ? frozenColumnWidth : candidate.x,
        y: rowOverlap > 0 ? frozenRowHeight : candidate.y,
    };
}

/**
 * Clamps a candidate drawn-line position (canvas units) so the line can never land inside the
 * frozen row/column, under any circumstances — the same reserved-chrome rule
 * resolveFrozenAreaCandidate enforces for cards, just simpler here since a line only has one
 * coordinate (where it crosses the perpendicular axis) rather than a full rect. Unlike a card
 * drop, a rejected click still produces a line: it's nudged flush against the frozen boundary
 * instead, so "click anywhere" always succeeds. `frozenColumnWidth`/`frozenRowHeight` are meant
 * to be called with TimelineCanvasHandle's own getFrozenColumnWidth()/getFrozenRowHeight() so a
 * pane mid-collapse-animation is judged against what's actually visible right now.
 */
export function resolveLinePosition(
    orientation: PlanLineOrientation,
    rawPosition: number,
    frozenColumnWidth: number = TIMELINE_FROZEN_COLUMN_WIDTH,
    frozenRowHeight: number = TIMELINE_FROZEN_ROW_HEIGHT,
): number {
    const frozenBound = orientation === 'vertical' ? frozenColumnWidth : frozenRowHeight;
    return Math.max(rawPosition, frozenBound);
}

/**
 * Computes the snapped drop-preview placement (and the ghost geometry that follows it) while a
 * drag is hovering the Timeline canvas. A card already on the canvas snaps to the nearest grid
 * cell at its existing size; a card arriving from the list previews at the default footprint,
 * centered under the cursor, snapped to the nearest cell. Either way the candidate is run
 * through resolveFrozenAreaCandidate so it can't land on the frozen row/column. Falls back to
 * `fallback`'s geometry (with no preview) if the canvas API can't resolve a point (e.g.
 * mid-unmount) or the candidate was rejected by the frozen-area guard.
 */
export function computeCanvasGhostPreview(
    api: TimelineCanvasHandle,
    origin: DragOriginKind,
    dragCanvasSize: { width: number; height: number } | null,
    clientX: number,
    clientY: number,
    fallback: { ghostLeft: number; ghostTop: number; ghostWidth: number; ghostHeight: number },
): CanvasGhostPreview {
    let canvasPreview: PlanCardPlacement | null = null;
    let rejected = false;
    // Read once per call so both branches below judge the candidate against the SAME live
    // (possibly mid-collapse-animation) reserved size — see resolveFrozenAreaCandidate's doc.
    const frozenColumnWidth = api.getFrozenColumnWidth();
    const frozenRowHeight = api.getFrozenRowHeight();

    if (origin === 'canvas' && dragCanvasSize) {
        const rawCanvasPoint = api.screenToCanvas(fallback.ghostLeft, fallback.ghostTop);
        if (rawCanvasPoint) {
            const snapped = api.snapPoint(rawCanvasPoint.x, rawCanvasPoint.y);
            const candidate = { x: snapped.x, y: Math.max(0, snapped.y), width: dragCanvasSize.width, height: dragCanvasSize.height };
            canvasPreview = resolveFrozenAreaCandidate(candidate, frozenColumnWidth, frozenRowHeight);
            rejected = canvasPreview === null;
        }
    } else {
        const cursorCanvasPoint = api.screenToCanvas(clientX, clientY);
        if (cursorCanvasPoint) {
            const rawX = cursorCanvasPoint.x - PLAN_CARD_DEFAULT_WIDTH / 2;
            const rawY = cursorCanvasPoint.y - PLAN_CARD_DEFAULT_HEIGHT / 2;
            const snapped = api.snapPoint(rawX, rawY);
            const candidate = { x: snapped.x, y: Math.max(0, snapped.y), width: PLAN_CARD_DEFAULT_WIDTH, height: PLAN_CARD_DEFAULT_HEIGHT };
            canvasPreview = resolveFrozenAreaCandidate(candidate, frozenColumnWidth, frozenRowHeight);
            rejected = canvasPreview === null;
        }
    }

    if (canvasPreview) {
        const screenRect = api.canvasRectToScreenRect(canvasPreview);
        if (screenRect) {
            return { canvasPreview, rejected, ghostLeft: screenRect.left, ghostTop: screenRect.top, ghostWidth: screenRect.width, ghostHeight: screenRect.height };
        }
    }
    return { canvasPreview, rejected, ...fallback };
}

/**
 * `placements` with the actively-dragged item's entry (if any) swapped for its live,
 * continuously-updating canvas position — `activeDrag.canvasPreview`, set on every mousemove by
 * PlanPage's own applyMove (see ActiveDrag's doc comment) — instead of the committed `placements`
 * state, which the ghost-drag architecture only writes to once the drag actually ends (see
 * finishDrag). Passed to TimelineCanvas as its `placements` prop so a connector attached to the
 * dragged card visibly follows it in real time. Safe to swap in wholesale, not just for connector
 * resolution: the dragged card's OWN rendering is separately suppressed via hiddenPlacementId
 * regardless of what this contains for it, so the only thing that actually changes is where
 * GridCanvas resolves a connector's endpoint from. `canvasPreview` is null whenever the drag
 * isn't currently over the canvas (or the pointer is over the frozen row/column) — regardless of
 * drag origin, since a card dragged in from the list can already be hovering an existing
 * connector's other end. Exported/pure for testability, mirroring resolveFrozenAreaCandidate/
 * computeCanvasGhostPreview above.
 */
export function resolveLivePlacements(
    placements: Record<string, PlanCardPlacement>,
    activeDrag: { itemId: string; canvasPreview: PlanCardPlacement | null } | null,
): Record<string, PlanCardPlacement> {
    if (!activeDrag?.canvasPreview) return placements;
    return { ...placements, [activeDrag.itemId]: activeDrag.canvasPreview };
}

/**
 * Index (within the OTHER cards actually rendered in `listEl`) the dragged item would land at if
 * dropped at `clientY` — counts how many items' vertical midpoints the cursor has passed.
 * `excludeItemId` omits the dragged item's own slot from the scan — its card stays rendered in
 * place during the drag (see PlanBoardColumn's elevatedItemId) rather than being removed from the
 * list, so without this exclusion the index would be computed against an array that still
 * contains the dragged item itself, throwing off the "index among the OTHER unplaced items"
 * number reinsertIntoPlanOrder expects. Pure/exported, like this file's other
 * small canvas-geometry derivations above, so it's unit-testable without mounting the page.
 */
export function computeListInsertIndexFromY(listEl: HTMLDivElement | null, clientY: number, excludeItemId?: string | null): number {
    if (!listEl) return 0;
    const itemEls = Array.from(listEl.querySelectorAll<HTMLElement>('[data-plan-item-id]'))
        .filter((el) => el.dataset.planItemId !== excludeItemId);
    const rects = itemEls.map((el, index) => {
        const r = el.getBoundingClientRect();
        return { index, top: r.top, height: r.height };
    });
    // getDragSortTargetIndex clamps to `totalItems - 1`, matching InsightsPanel/TriagePanel's own
    // usage where `totalItems` is the FULL list (the dragged item's own slot included, since it's
    // reordering among a fixed N slots). Here `itemEls` has already excluded the dragged item (or
    // never included it, for a card arriving from the canvas), so the valid landing positions
    // span 0..itemEls.length inclusive (itemEls.length + 1 of them: before/after/between every
    // OTHER item) rather than 0..itemEls.length - 1.
    return getDragSortTargetIndex(clientY, rects, itemEls.length + 1);
}

/**
 * Height (px) the dragged item would render at as a list card. Prefers `cachedHeight` — the
 * value captured once at drag start via a hidden probe render (see PlanPage's startRealDrag) —
 * over reading `probeEl` directly, which would race the probe's own React mount/commit on the
 * very first call and silently fall through to the placeholder constant: exactly the "wrong
 * height, then corrects" glitch the probe exists to avoid.
 */
export function measureListCardHeight(listEl: HTMLDivElement | null, cachedHeight: number | null, probeEl: HTMLDivElement | null): number {
    if (cachedHeight) return cachedHeight;
    const probeHeight = probeEl?.getBoundingClientRect().height;
    if (probeHeight) return probeHeight;
    const firstItem = listEl?.querySelector<HTMLElement>('[data-plan-item-id]');
    return firstItem?.getBoundingClientRect().height ?? PLAN_BOARD_COLUMN_PLACEHOLDER_HEIGHT;
}

/**
 * Screen rect a card would settle into if inserted at `index` among `listEl`'s OTHER rendered
 * items (see computeListInsertIndexFromY's own `excludeItemId` doc). Used both live during a drag
 * (to size the ghost while hovering a list) and once at drop (as beginSettle's cross-context
 * target rect) — see PlanPage's applyMove/finishDrag.
 */
export function estimateListSlotRect(listEl: HTMLDivElement | null, probeHeight: number | undefined, index: number, excludeItemId?: string | null): ScreenRect {
    const bounds = listEl?.getBoundingClientRect() ?? null;
    const itemEls = listEl
        ? Array.from(listEl.querySelectorAll<HTMLElement>('[data-plan-item-id]')).filter((el) => el.dataset.planItemId !== excludeItemId)
        : [];

    const target = itemEls[index];
    if (target) {
        const r = target.getBoundingClientRect();
        return { left: r.left, top: r.top, width: r.width, height: probeHeight ?? r.height };
    }
    const last = itemEls[itemEls.length - 1];
    if (last) {
        const r = last.getBoundingClientRect();
        return { left: r.left, top: r.bottom + 8, width: r.width, height: probeHeight ?? PLAN_BOARD_COLUMN_PLACEHOLDER_HEIGHT };
    }
    if (bounds && listEl) {
        // No items to anchor against (e.g. the dragged card is the only one, so the list is
        // currently empty): the first real child lands flush with the list's own content box —
        // .plan-column__list has a uniform 2px padding on every side (see app.css) and no gap
        // applies before the first child. Guessing an arbitrary inset here (e.g. +4px) instead of
        // these exact values is what caused the settle target to land a few pixels off from
        // where the real card then mounts, producing a visible "jump then correct" right after
        // the drop. Width comes from clientWidth (not getBoundingClientRect().width)
        // specifically because `scrollbar-gutter: stable` reserves extra space that only
        // clientWidth accounts for. The card's own margin-right (5px, see .plan-board-card--list)
        // also has to be subtracted here — align-items:stretch shrinks the mounted card's box by
        // its own margin, so omitting it left the ghost settling 5px too wide, visible as the
        // right edge snapping inward right after drop.
        return {
            left: bounds.left + 2,
            top: bounds.top + 2,
            width: listEl.clientWidth - 2 - 2 - 5,
            height: probeHeight ?? PLAN_BOARD_COLUMN_PLACEHOLDER_HEIGHT,
        };
    }
    return { left: 0, top: 0, width: 260, height: probeHeight ?? PLAN_BOARD_COLUMN_PLACEHOLDER_HEIGHT };
}

export default function PlanPage() {
    // Every story/experiment across the project's trees — see enablerCards below for the other
    // source of Plan board cards (user-created, no OST involved at all).
    const ostItems = usePlanBoardItems();

    // Pushing the placed cards and their connectors to GitHub. The hook owns the whole action —
    // gating, the request, and folding the resulting link map back into the store — so this page
    // only places the button and the report modal.
    const planSync = usePlanSync();

    const user = useStore((s) => s.user);
    const activeProject = useStore((s) => s.activeProject);
    const storedPlanOrder = useStore((s) => s.planOrder);
    const storedPlanPlacements = useStore((s) => s.planPlacements);
    const storedPlanLines = useStore((s) => s.planLines);
    const storedPlanAxisLabels = useStore((s) => s.planAxisLabels);
    const storedPlanZoom = useStore((s) => s.planZoom);
    const storedPlanConnections = useStore((s) => s.planConnections);
    const storedPlanEnablerCards = useStore((s) => s.planEnablerCards);
    const setPlanBoardData = useStore((s) => s.setPlanBoardData);

    // User-created Enabler cards (see EnablerCardItem, types.ts) — same seed-from-store,
    // debounced-persist pattern as placements/planOrder/lines below. Unlike ostItems above, this
    // IS the card content itself (title/description), not just a reference to it, since an
    // Enabler card has no OST node to reference.
    const [enablerCards, setEnablerCards] = useState<EnablerCardItem[]>(storedPlanEnablerCards);
    const [isEnablerModalOpen, setIsEnablerModalOpen] = useState(false);

    // Every card the Plan board can show, OST-sourced and user-created alike — the Plan column's
    // source list and, by id, the Timeline canvas's lookup (allItemsById below, connector color
    // resolution, the drag ghost's item, etc.). An Enabler card's GitHub link is folded in here
    // the same way usePlanBoardItems folds it into an OST item, since it reads from the same
    // project-level externalLinks map (see usePlanBoardItems.ts's own doc comment).
    const externalLinks = activeProject?.externalLinks?.items;
    const allItems: PlanBoardItem[] = useMemo(() => {
        if (!externalLinks) return [...ostItems, ...enablerCards];
        return [...ostItems, ...enablerCards.map((card) => ({ ...card, link: externalLinks[card.id] }))];
    }, [ostItems, enablerCards, externalLinks]);
    const allItemsById = useMemo(() => new Map(allItems.map((it) => [it.id, it])), [allItems]);

    // Seeded from the store (already hydrated by ProjectDataLoader/useProjectData before
    // this page ever mounts — see useProjectData.ts). Kept as local state, not read from
    // the store on every render, because drag interactions below update it many times per
    // second; the debounced effect further down mirrors it back into the store for
    // persistence and so it survives navigating away and back within the same session.
    const [planOrder, setPlanOrder] = useState<string[]>(storedPlanOrder);
    const [placements, setPlacements] = useState<Record<string, PlanCardPlacement>>(storedPlanPlacements);
    const [pressedItemId, setPressedItemId] = useState<string | null>(null);
    const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
    const [settleAnim, setSettleAnim] = useState<SettleAnim | null>(null);
    // Toggled by clicking a card (in either the Plan panel or the Timeline canvas) — cleared by
    // clicking a different card, the same card again, or either panel's empty background.
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

    // User-drawn vertical/horizontal lines on the Timeline grid — same seed-from-store,
    // debounced-persist pattern as placements/planOrder above.
    const [lines, setLines] = useState<PlanDrawnLine[]>(storedPlanLines);
    // Non-null while the "click to place a line" tool is armed (a line-menu item was clicked but
    // the placing click on the canvas hasn't landed yet) — swaps the canvas cursor to crosshair
    // and reinterprets the next background click as a placement instead of a deselect.
    const [linePlacementMode, setLinePlacementMode] = useState<PlanLineOrientation | null>(null);
    // Toggled by clicking a drawn line — shows the color picker popover anchored near the click.
    const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
    const [lineColorPickerAnchor, setLineColorPickerAnchor] = useState<{ left: number; top: number } | null>(null);

    // Toggled by clicking a rendered card connection (see handleConnectorMouseDown) — shown
    // emphasized (see GridCanvas.tsx's CONNECTOR_SELECTED_STROKE_SCALE and friends) and removable
    // via Delete/Backspace (see the keydown effect below). Also anchors the relationship-type
    // popover (see connectionPopoverAnchor just below) at the point it was clicked.
    const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
    // Screen-space point the relationship-type popover is anchored above — set together with
    // selectedConnectionId by handleConnectorMouseDown. Kept as a SEPARATE piece of state (rather
    // than deriving "show the popover" purely from selectedConnectionId) so the click coordinates
    // travel alongside the selection, same as lineColorPickerAnchor does for selectedLineId.
    const [connectionPopoverAnchor, setConnectionPopoverAnchor] = useState<{ left: number; top: number } | null>(null);
    // The popover only ever makes sense while a connection is selected, so clear it the instant
    // the selection clears from ANY cause (background click, Escape, Delete, outside click, or
    // the connection being pruned because its item was deleted elsewhere) rather than repeating
    // "also clear the anchor" at every one of those call sites.
    useEffect(() => {
        if (!selectedConnectionId) setConnectionPopoverAnchor(null);
    }, [selectedConnectionId]);

    // User-entered name/color labels for the Timeline's frozen row/column segments (see
    // PlanAxisLabel, types.ts) — same seed-from-store, debounced-persist pattern as
    // placements/planOrder/lines above.
    const [axisLabels, setAxisLabels] = useState<PlanAxisLabel[]>(storedPlanAxisLabels);

    // The Timeline canvas's last-used zoom level — same seed-from-store, debounced-persist
    // pattern as the rest of this page's Plan data. Undefined (never set) is passed straight
    // through as TimelineCanvas's `initialZoom`, which itself falls back to the fit-to-width
    // zoom in that case — so a project that's never had its zoom saved behaves exactly as before
    // this existed. Updated via TimelineCanvas's onZoomChange, which fires on every zoom change
    // (not just once it settles) — cheap, since this is just local state until the debounced
    // effect below actually persists it.
    const [zoom, setZoom] = useState<number | undefined>(storedPlanZoom);

    // Ctrl+drag connections between placed Timeline cards (see PlanBoardConnection, types.ts) —
    // same seed-from-store, debounced-persist pattern as the rest of this page's Plan data. A
    // connection whose endpoint isn't currently placed simply doesn't render — GridCanvas's own
    // `connectors` rendering already skips any connector referencing an unplaced id — rather than
    // being pruned outright here, so it reappears if the card is placed again.
    const [connections, setConnections] = useState<PlanBoardConnection[]>(storedPlanConnections);

    const planColumnListRef = useRef<HTMLDivElement>(null);
    const timelineCanvasRef = useRef<TimelineCanvasHandle>(null);
    // Off-screen (visibility:hidden) render of the dragged item as a list card, used purely to
    // measure the height it would actually render at — far more accurate than guessing from a
    // sibling (which may have different description length) or a hardcoded fallback constant.
    const listProbeRef = useRef<HTMLDivElement>(null);
    // The probe's measured height, captured once per drag (via flushSync — see startRealDrag)
    // so it's available from the very first frame instead of racing the probe's own mount.
    const cachedListHeightRef = useRef<number | null>(null);
    const placementsRef = useRef(placements);
    const unplacedItemsRef = useRef<PlanBoardItem[]>([]);
    const linesRef = useRef(lines);
    const connectionsRef = useRef(connections);

    useEffect(() => { placementsRef.current = placements; }, [placements]);
    useEffect(() => { linesRef.current = lines; }, [lines]);
    useEffect(() => { connectionsRef.current = connections; }, [connections]);

    // Same custom open-hand cursor the OST canvas shows on its nodes (see CanvasNode.tsx) —
    // applied to <body> for the duration of a real drag (past the movement threshold; see
    // activeDrag) so it stays correct no matter what the pointer is actually over (the ghost
    // placeholder itself is pointer-events:none — see the ghost render below). Deliberately the
    // plain hand, not the pinch cursor — that's reserved for a different gesture on this canvas.
    // The .dragging-grab class forces every descendant to inherit body's cursor so a per-element
    // `cursor` declaration underneath the pointer can't override it mid-drag.
    // Keyed on the boolean, not activeDrag itself, since activeDrag's position fields update on
    // every mousemove while dragging — this effect only needs to fire on the drag start/end edges.
    const isDragging = !!activeDrag;
    useEffect(() => {
        if (!isDragging) return;
        const grabCursor = `url(${process.env.PUBLIC_URL || ''}/hand-cursor.cur) 8 8, grab`;
        document.body.style.cursor = grabCursor;
        document.body.classList.add('dragging-grab');
        return () => {
            document.body.style.cursor = '';
            document.body.classList.remove('dragging-grab');
        };
    }, [isDragging]);

    // Debounced (600 ms) persist of card placements/order/lines/axisLabels/zoom/connections/
    // enablerCards to the backend, mirroring ProjectPage's OST auto-persist. Also mirrors the
    // values into the store so they survive navigating away and back within the same session (see
    // useProjectData.ts hydration). No mount-settling guard: planOrder/placements/lines/
    // axisLabels/zoom/connections/enablerCards are seeded from already-hydrated store data (see
    // the useState initializers above), so the very first save this schedules is just an
    // idempotent echo of what the server already has — harmless, and simpler than risking a real
    // edit landing in a settling window and never getting persisted.
    useEffect(() => {
        if (!user || !activeProject) return;

        const timeout = setTimeout(async () => {
            setPlanBoardData({ placements, order: planOrder, lines, axisLabels, zoom, connections, enablerCards });
            const result = await savePlanBoard(activeProject._id, placements, planOrder, lines, axisLabels, zoom, connections, enablerCards);
            if (isErrorResponse(result)) {
                console.error('Plan board save failed:', result.error);
            }
        }, 600);
        return () => clearTimeout(timeout);
    }, [user, activeProject, placements, planOrder, lines, axisLabels, zoom, connections, enablerCards, setPlanBoardData]);

    // Keep planOrder in sync with the derived item list: preserve existing order,
    // append newly-appeared items, drop ids for nodes that no longer exist.
    useEffect(() => {
        setPlanOrder((prev) => {
            const existingIds = new Set(allItems.map((it) => it.id));
            const kept = prev.filter((id) => existingIds.has(id));
            const keptSet = new Set(kept);
            const added = allItems.filter((it) => !keptSet.has(it.id)).map((it) => it.id);
            if (added.length === 0 && kept.length === prev.length) return prev;
            return [...kept, ...added];
        });
    }, [allItems]);

    // Drop placements for nodes that no longer exist (e.g. the story was deleted elsewhere).
    useEffect(() => {
        setPlacements((prev) => {
            const existingIds = new Set(allItems.map((it) => it.id));
            let changed = false;
            const next: Record<string, PlanCardPlacement> = {};
            for (const [id, placement] of Object.entries(prev)) {
                if (existingIds.has(id)) next[id] = placement;
                else changed = true;
            }
            return changed ? next : prev;
        });
    }, [allItems]);

    // Drop connections referencing an item that no longer exists (e.g. the story was deleted
    // elsewhere) — mirrors the placements-pruning effect above. A connection whose item still
    // exists but is merely unplaced is left alone (see `connections`'s own doc comment above).
    useEffect(() => {
        setConnections((prev) => {
            const existingIds = new Set(allItems.map((it) => it.id));
            const next = prev.filter((c) => existingIds.has(c.fromItemId) && existingIds.has(c.toItemId));
            return next.length === prev.length ? prev : next;
        });
    }, [allItems]);

    // Clear the selection if its item no longer exists (e.g. deleted elsewhere).
    useEffect(() => {
        setSelectedItemId((prev) => (prev && !allItemsById.has(prev) ? null : prev));
    }, [allItemsById]);

    // Clear a selected connection if it was just pruned above (its item was deleted elsewhere).
    useEffect(() => {
        setSelectedConnectionId((prev) => (prev && !connections.some((c) => c.id === prev) ? null : prev));
    }, [connections]);

    const unplacedItems = useMemo(
        () => planOrder
            .map((id) => allItemsById.get(id))
            .filter((it): it is PlanBoardItem => !!it && !placements[it.id]),
        [planOrder, allItemsById, placements],
    );
    useEffect(() => { unplacedItemsRef.current = unplacedItems; }, [unplacedItems]);

    const reinsertIntoPlanOrder = useCallback((itemId: string, targetIndexAmongUnplaced: number) => {
        setPlanOrder((prev) => {
            const withoutItem = prev.filter((id) => id !== itemId);
            let seen = 0;
            let insertAt = withoutItem.length;
            for (let i = 0; i < withoutItem.length; i++) {
                const id = withoutItem[i];
                if (!placementsRef.current[id]) {
                    if (seen === targetIndexAmongUnplaced) {
                        insertAt = i;
                        break;
                    }
                    seen++;
                }
            }
            const next = [...withoutItem];
            next.splice(insertAt, 0, itemId);
            return next;
        });
    }, []);

    const beginSettle = useCallback((itemId: string, to: ScreenRect, onSettled: () => void) => {
        setSettleAnim({ itemId, to, onSettled });
        // setTimeout (not requestAnimationFrame) drives the commit: rAF can stall indefinitely
        // in a backgrounded/unfocused tab, which would otherwise leave the ghost stuck forever
        // with the card showing nowhere. The ghost's width/height transition is already
        // continuously active (GHOST_RESIZE_TRANSITION), so simply setting `to` here is enough
        // to animate it — no separate rAF-timed phase toggle needed.
        window.setTimeout(() => {
            onSettled();
            setSettleAnim(null);
        }, SETTLE_COMMIT_DELAY_MS);
    }, []);

    /**
     * Unplaces an item that's currently on the Timeline canvas, settling it back into the Plan
     * column at `targetIndex` (defaulting to the end of the unplaced list) — the same settle
     * animation + commit sequence finishDrag uses for a canvas→list drag release (see its
     * cross-context, ending-on-list branch below), extracted so it's also callable with no drag in
     * progress at all, e.g. a tree-sourced Enabler card's cross button (see
     * handleRemoveEnablerCard).
     */
    const sendItemBackToList = useCallback((itemId: string, targetIndex?: number) => {
        const resolvedTargetIndex = targetIndex ?? unplacedItemsRef.current.length;
        const ownListEl = planColumnListRef.current;
        const targetScreenRect = estimateListSlotRect(ownListEl, cachedListHeightRef.current ?? listProbeRef.current?.getBoundingClientRect().height, resolvedTargetIndex, itemId);
        beginSettle(itemId, targetScreenRect, () => {
            setPlacements((p) => {
                const next = { ...p };
                delete next[itemId];
                return next;
            });
            // Sending a card back to its panel breaks any connector it had on the canvas —
            // see pruneConnectionsForUnplacedItem's doc comment.
            setConnections((prev) => pruneConnectionsForUnplacedItem(prev, itemId));
            reinsertIntoPlanOrder(itemId, resolvedTargetIndex);
        });
    }, [beginSettle, reinsertIntoPlanOrder]);

    const finishDrag = useCallback((current: ActiveDrag) => {
        // A hover rejected by the frozen-area guard (see resolveFrozenAreaCandidate) is handled
        // up front, before the same-/cross-context logic below, because the two origins need
        // genuinely different fallbacks rather than just "not ending on canvas":
        //  - list origin: the card was never placed, so falling through to the ordinary
        //    same-context list-release path (below) correctly leaves it in the Plan panel.
        //  - canvas origin: the card IS already placed. Falling through the same way would
        //    treat this as a canvas→list move — deleting its placement and inserting it into
        //    the Plan panel, which is wrong (it never left the canvas) and, combined with the
        //    settle animation targeting a list slot it was never dragged over, is what made the
        //    card vanish from both panels instead of snapping back to where it started. Simply
        //    doing nothing here is correct: its placement was never written to during the drag,
        //    so it's already sitting at its original spot.
        if (current.canvasDropRejected) {
            if (current.origin === 'list') {
                const targetIndex = current.listInsertIndex ?? unplacedItemsRef.current.length;
                reinsertIntoPlanOrder(current.itemId, targetIndex);
            }
            return;
        }

        const wasOnCanvas = current.origin === 'canvas';
        const endingOnCanvas = current.overCanvas;

        if (wasOnCanvas === endingOnCanvas) {
            // Same-context release: commit directly, no cross-context settle needed.
            if (endingOnCanvas) {
                if (current.canvasPreview) {
                    const placement = current.canvasPreview;
                    setPlacements((p) => ({ ...p, [current.itemId]: placement }));
                }
            } else {
                const targetIndex = current.listInsertIndex ?? unplacedItemsRef.current.length;
                reinsertIntoPlanOrder(current.itemId, targetIndex);
            }
            return;
        }

        const fallbackRect: ScreenRect = { left: current.ghostLeft, top: current.ghostTop, width: current.ghostWidth, height: current.ghostHeight };

        if (endingOnCanvas) {
            const targetPlacement = current.canvasPreview ?? { x: 0, y: 0, width: PLAN_CARD_DEFAULT_WIDTH, height: PLAN_CARD_DEFAULT_HEIGHT };
            const targetScreenRect = timelineCanvasRef.current?.canvasRectToScreenRect(targetPlacement) ?? fallbackRect;
            beginSettle(current.itemId, targetScreenRect, () => {
                setPlacements((p) => ({ ...p, [current.itemId]: targetPlacement }));
            });
        } else {
            const targetIndex = current.listInsertIndex ?? unplacedItemsRef.current.length;
            sendItemBackToList(current.itemId, targetIndex);
        }
    }, [beginSettle, sendItemBackToList, reinsertIntoPlanOrder]);

    const beginCardDrag = useCallback((e: React.MouseEvent, itemId: string, origin: DragOriginKind) => {
        e.preventDefault();
        e.stopPropagation();
        const startClientX = e.clientX;
        const startClientY = e.clientY;
        const sourceEl = origin === 'list' ? (e.currentTarget as HTMLElement) : null;

        setPressedItemId(itemId);

        let live: ActiveDrag | null = null;
        let dragCanvasSize: { width: number; height: number } | null = null;
        // The card's own "resting" size for its origin context — what the ghost falls back
        // to while in transit over neither the list nor the canvas.
        let naturalWidth = 0;
        let naturalHeight = 0;

        // Only spawns the floating ghost (hiding the source, detaching from list/canvas flow)
        // once the pointer has actually moved — a plain click never triggers it.
        const startRealDrag = (): boolean => {
            const canvasApi = timelineCanvasRef.current;
            let startRect: ScreenRect | null = null;

            if (origin === 'list') {
                const r = sourceEl?.getBoundingClientRect();
                if (!r) return false;
                startRect = { left: r.left, top: r.top, width: r.width, height: r.height };
            } else {
                const placement = placementsRef.current[itemId];
                if (!placement || !canvasApi) return false;
                const r = canvasApi.canvasRectToScreenRect(placement);
                if (!r) return false;
                startRect = r;
                dragCanvasSize = { width: placement.width, height: placement.height };
            }

            naturalWidth = startRect.width;
            naturalHeight = startRect.height;
            const pointerOffsetX = startClientX - startRect.left;
            const pointerOffsetY = startClientY - startRect.top;

            live = {
                itemId,
                origin,
                pointerOffsetX,
                pointerOffsetY,
                ghostLeft: startRect.left,
                ghostTop: startRect.top,
                ghostWidth: startRect.width,
                ghostHeight: startRect.height,
                overCanvas: origin === 'canvas',
                canvasDropRejected: false,
                overList: origin === 'list',
                listInsertIndex: origin === 'list' ? computeListInsertIndexFromY(planColumnListRef.current, startClientY, itemId) : null,
                listInsertHeight: origin === 'list' ? startRect.height : null,
                canvasPreview: origin === 'canvas' ? placementsRef.current[itemId] ?? null : null,
            };
            setPressedItemId(null);

            // flushSync forces this state update (and the hidden list-probe it causes to mount —
            // see the JSX below) to commit to the DOM synchronously, right here, instead of on
            // React's next scheduled tick. That lets us measure the probe's true rendered height
            // immediately below rather than racing its mount on a later, unpredictably-timed
            // render — which is exactly what caused the settle target to briefly use the wrong
            // (fallback) height before "correcting" once the probe caught up.
            cachedListHeightRef.current = null;
            flushSync(() => setActiveDrag(live));
            cachedListHeightRef.current = listProbeRef.current?.getBoundingClientRect().height ?? null;
            return true;
        };

        // Shared by mousemove AND mouseup: some synthetic/automated input (and very fast real
        // drags the OS coalesces) can deliver a mousedown then a mouseup at a far-away position
        // with NO mousemove in between — without also running this from onUp, such a drag would
        // never cross the threshold and would silently no-op.
        const applyMove = (clientX: number, clientY: number) => {
            if (!live) {
                const dx = clientX - startClientX;
                const dy = clientY - startClientY;
                if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
                if (!startRealDrag()) return;
            }
            const current = live;
            if (!current) return;

            const canvasBounds = timelineCanvasRef.current?.getWrapperRect() ?? null;
            const ownListEl = planColumnListRef.current;
            const overCanvas = hitTestRect(canvasBounds, clientX, clientY);
            const overList = !overCanvas && hitTestRect(ownListEl?.getBoundingClientRect() ?? null, clientX, clientY);
            const api = timelineCanvasRef.current;

            // Position always tracks the live target exactly (raw cursor in transit, or the
            // snapped cell/slot once over a region) — it is NEVER CSS-transitioned, so it's
            // always instant, however the target is computed. Size defaults to the card's
            // natural resting size and is overridden below when hovering a region; because
            // width/height ARE continuously transitioned (GHOST_RESIZE_TRANSITION) but this
            // value only actually changes when the target region/cell changes (not on every
            // pixel of movement), the resize animates smoothly exactly once per crossing.
            let ghostLeft = clientX - current.pointerOffsetX;
            let ghostTop = clientY - current.pointerOffsetY;
            let ghostWidth = naturalWidth;
            let ghostHeight = naturalHeight;
            let canvasPreview: PlanCardPlacement | null = null;
            let canvasDropRejected = false;
            let listInsertIndex: number | null = null;
            let listInsertHeight: number | null = null;

            if (overCanvas && api) {
                const preview = computeCanvasGhostPreview(api, origin, dragCanvasSize, clientX, clientY, { ghostLeft, ghostTop, ghostWidth, ghostHeight });
                canvasPreview = preview.canvasPreview;
                canvasDropRejected = preview.rejected;
                ghostLeft = preview.ghostLeft;
                ghostTop = preview.ghostTop;
                ghostWidth = preview.ghostWidth;
                ghostHeight = preview.ghostHeight;
            } else if (overList) {
                listInsertIndex = computeListInsertIndexFromY(ownListEl, clientY, itemId);
                listInsertHeight = measureListCardHeight(ownListEl, cachedListHeightRef.current, listProbeRef.current);
                // Only the SIZE targets the slot (so returning-from-canvas still grows smoothly
                // to list-card size) — position deliberately keeps raw-following the cursor
                // rather than locking to the slot's rect. With few/no visible siblings (e.g.
                // the dragged item is the only one), the slot rect collapses to a single fixed
                // position at the top of the list; snapping the ghost to it made reordering feel
                // "stuck" — no visual response to up/down movement until crossing into a
                // different slot. The drop-placeholder gap (PlanBoardColumn) already shows where
                // it will land, so the ghost itself doesn't need to be pinned there too.
                const slot = estimateListSlotRect(ownListEl, cachedListHeightRef.current ?? listProbeRef.current?.getBoundingClientRect().height, listInsertIndex, itemId);
                ghostWidth = slot.width;
                ghostHeight = slot.height;
            }

            live = { ...current, ghostLeft, ghostTop, ghostWidth, ghostHeight, overCanvas, canvasDropRejected, overList, listInsertIndex, listInsertHeight, canvasPreview };
            setActiveDrag(live);
        };

        const onMove = (mv: MouseEvent) => applyMove(mv.clientX, mv.clientY);

        const onUp = (mv: MouseEvent) => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            applyMove(mv.clientX, mv.clientY);
            setPressedItemId(null);
            if (!live) {
                // Never exceeded the drag threshold — treat as a plain click: toggle selection.
                setSelectedItemId((prev) => (prev === itemId ? null : itemId));
                setSelectedConnectionId(null);
                return;
            }
            setActiveDrag(null);
            finishDrag(live);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [finishDrag]);

    const handleListCardMouseDown = useCallback((e: React.MouseEvent, itemId: string) => {
        beginCardDrag(e, itemId, 'list');
    }, [beginCardDrag]);

    /**
     * OST-agnostic reuse of useConnectDrag (see Canvas.tsx for the OST canvas's own instance):
     * source/target/point tracking and the window mousemove/mouseup lifecycle are identical; what
     * differs is layered on top here — no structural parent/child EXCLUSION check (Plan cards
     * have no separate tree to conflict with, unlike OST's Canvas.tsx), just a self-connection
     * guard and a from→to dedup check, then a PlanBoardConnection instead of a NodeConnection.
     * `sourceItemId` becomes `fromItemId` and is treated as the connection's implicit PARENT;
     * `targetItemId` becomes `toItemId`, the implicit CHILD — see PlanBoardConnection's doc
     * comment (types.ts) for what that and the separately user-assigned relationshipType mean.
     */
    const handleConnectFinish = useCallback((sourceItemId: string, targetItemId: string | null, start: { x: number; y: number }, end: { x: number; y: number }) => {
        if (!targetItemId || targetItemId === sourceItemId) return;
        const sourcePlacement = placementsRef.current[sourceItemId];
        const targetPlacement = placementsRef.current[targetItemId];
        if (!sourcePlacement || !targetPlacement) return;

        const existing = connectionsRef.current.some((c) => c.fromItemId === sourceItemId && c.toItemId === targetItemId);
        if (existing) return;

        const connection: PlanBoardConnection = {
            id: randomId(),
            fromItemId: sourceItemId,
            toItemId: targetItemId,
            fromOffset: { x: start.x - sourcePlacement.x, y: start.y - sourcePlacement.y },
            toOffset: { x: end.x - targetPlacement.x, y: end.y - targetPlacement.y },
            // Defaults to 'blocks' rather than staying undefined — a freshly drawn connector
            // is a real, typed relationship from the moment it's drawn, not an unset one waiting
            // on the popover; the user can still change it via PlanConnectionRelationshipPopover.
            relationshipType: 'blocks',
        };
        setConnections((prev) => [...prev, connection]);
    }, []);

    const connectDragScreenToCanvas = useCallback(
        (clientX: number, clientY: number) => timelineCanvasRef.current?.screenToCanvas(clientX, clientY) ?? null,
        [],
    );
    const connectDrag = useConnectDrag({ screenToCanvas: connectDragScreenToCanvas, onFinish: handleConnectFinish });
    // Ctrl on Windows/Linux, Meta/Cmd on Mac — drives the pinch-cursor hover affordance on
    // Timeline cards (see PlanBoardCard's modifierKeyActive prop), same as the OST canvas's own
    // Canvas.tsx instance of this hook.
    const modifierKeyActive = useModifierKeyActive();

    const handleCardConnectHover = useCallback((itemId: string) => {
        if (!connectDrag.active || itemId === connectDrag.sourceId) return;
        connectDrag.setHoverTarget(itemId);
    }, [connectDrag]);

    const handleCardConnectLeave = useCallback(() => {
        if (connectDrag.active) connectDrag.setHoverTarget(null);
    }, [connectDrag]);

    // Resolved CanvasConnector[]/CanvasConnectorPreview for GridCanvas (see TimelineCanvas.tsx) —
    // color mirrors buildEdgePath's own "color driven by the FROM node's type" convention (OST's
    // tree-edge routine), reusing the same COLORS map rather than a separate Plan-specific palette.
    // Both PlanBoardItem types a Plan card can be ('story'/'experiment') already have an entry.
    // Used as the fallback for a connection with no relationshipType yet (see `connectors` below).
    const connectorColorForItem = useCallback((itemId: string): string => {
        const item = allItemsById.get(itemId);
        return (item && COLORS[item.type]?.border) || '#f55d0b';
    }, [allItemsById]);

    // Once a connection has a user-assigned relationshipType (see PlanConnectionRelationshipPopover),
    // its arrow recolors to that type's color, taking over from the per-source-item-type default.
    const connectors: CanvasConnector[] = useMemo(
        () => connections.map((c) => ({
            id: c.id,
            fromId: c.fromItemId,
            toId: c.toItemId,
            color: resolveConnectorColor(c, connectorColorForItem(c.fromItemId)),
        })),
        [connections, connectorColorForItem],
    );

    const connectorPreview: CanvasConnectorPreview | null = useMemo(() => {
        if (!connectDrag.active || !connectDrag.sourceId || !connectDrag.startPoint || !connectDrag.currentPoint) return null;
        return { from: connectDrag.startPoint, to: connectDrag.currentPoint, color: connectorColorForItem(connectDrag.sourceId) };
    }, [connectDrag.active, connectDrag.sourceId, connectDrag.startPoint, connectDrag.currentPoint, connectorColorForItem]);

    const handleCanvasCardMouseDown = useCallback((e: React.MouseEvent, itemId: string) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            connectDrag.start(itemId, e.clientX, e.clientY);
            return;
        }
        beginCardDrag(e, itemId, 'canvas');
    }, [beginCardDrag, connectDrag]);

    /** Selects a connector on click — clears any other selection first, mirroring
     * handleLineMouseDown's own click branch — and anchors the relationship-type popover
     * (connectionPopoverAnchor) at the click point. Re-clicking an already-selected connector
     * just re-anchors the popover at the new point, same as handleLineMouseDown's own click
     * branch repositioning its popovers. Deletion still happens via Delete/Backspace while
     * selected (see the keydown effect below), unaffected by the popover. */
    const handleConnectorMouseDown = useCallback((connectionId: string, e: React.MouseEvent<SVGPathElement>) => {
        setSelectedItemId(null);
        setSelectedLineId(null);
        setLineColorPickerAnchor(null);
        setSelectedConnectionId(connectionId);
        setConnectionPopoverAnchor({ left: e.clientX, top: e.clientY });
    }, []);

    /** Assigns or changes the selected connection's relationshipType — see
     * PlanConnectionRelationshipPopover. Persisted via the same debounced effect as every other
     * piece of `connections` state. */
    const handleConnectionRelationshipTypeChange = useCallback((connectionId: string, relationshipType: PlanConnectionRelationshipType) => {
        setConnections((prev) => prev.map((c) => (c.id === connectionId ? { ...c, relationshipType } : c)));
    }, []);

    /**
     * Drags one of a selected canvas card's four edge resize strips, or one of its four corner
     * handles for a simultaneous width+height resize (see PlanBoardCard.tsx). Recomputed from
     * the placement's ORIGINAL x/y/width/height and the total cursor delta every move, not
     * accumulated frame to frame — mirrors handleLineMouseDown's own math below. Growing from
     * the right/bottom edge only changes width/height; growing from the left/top edge also has
     * to shift x/y by the same amount the (snapped) size actually changed, so the OPPOSITE edge
     * stays anchored in place. A corner (e.g. 'bottom-right') combines both its edges' effects —
     * checking with .includes() against the hyphenated edge name handles both plain edges and
     * corners with the same branches, rather than needing a separate diagonal-specific path.
     */
    const handleResizeHandleMouseDown = useCallback((e: React.MouseEvent, itemId: string, edge: ResizeEdge) => {
        e.preventDefault();
        e.stopPropagation();
        const canvasApi = timelineCanvasRef.current;
        const start = placementsRef.current[itemId];
        if (!canvasApi || !start) return;

        const zoom = canvasApi.getZoom();
        const startMouseX = e.clientX;
        const startMouseY = e.clientY;

        const onMove = (mv: MouseEvent) => {
            const dxCanvas = (mv.clientX - startMouseX) / zoom;
            const dyCanvas = (mv.clientY - startMouseY) / zoom;

            let x = start.x;
            let y = start.y;
            let width = start.width;
            let height = start.height;

            if (edge.includes('right')) width = start.width + dxCanvas;
            if (edge.includes('left')) width = start.width - dxCanvas;
            if (edge.includes('bottom')) height = start.height + dyCanvas;
            if (edge.includes('top')) height = start.height - dyCanvas;

            const snappedSize = canvasApi.snapSize(width, height);
            if (edge.includes('left')) x = start.x + start.width - snappedSize.width;
            if (edge.includes('top')) y = start.y + start.height - snappedSize.height;

            setPlacements((p) => ({ ...p, [itemId]: { x, y: Math.max(0, y), width: snappedSize.width, height: snappedSize.height } }));
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, []);

    /**
     * Creates a new Enabler card (see AddEnablerCardModal) already placed on the Timeline canvas,
     * centered in whatever part of the grid is currently visible — there's no drop cursor to
     * center it under, unlike a card dragged in from the Plan list. Snapped and run through the
     * same frozen-area guard as a dragged-in card (resolveFrozenAreaCandidate) so it can't land on
     * the frozen row/column either. Once added to `enablerCards`, it flows into `allItems` (see
     * above) and from there into planOrder/placements pruning, connections, GitHub sync, and
     * every other Plan-board operation exactly like an OST-sourced card — no separate wiring
     * needed for any of that.
     */
    const handleCreateEnablerCard = useCallback((title: string, description: string) => {
        const id = randomId();
        const newCard: EnablerCardItem = { id, type: 'enabler', title, description, treeId: null, treeName: null };

        const api = timelineCanvasRef.current;
        let placement: PlanCardPlacement = { x: 0, y: 0, width: PLAN_CARD_DEFAULT_WIDTH, height: PLAN_CARD_DEFAULT_HEIGHT };
        const wrapperRect = api?.getWrapperRect();
        const centerPoint = api && wrapperRect
            ? api.screenToCanvas(wrapperRect.left + wrapperRect.width / 2, wrapperRect.top + wrapperRect.height / 2)
            : null;
        if (api && centerPoint) {
            const rawX = centerPoint.x - PLAN_CARD_DEFAULT_WIDTH / 2;
            const rawY = centerPoint.y - PLAN_CARD_DEFAULT_HEIGHT / 2;
            const snapped = api.snapPoint(rawX, rawY);
            const candidate: PlanCardPlacement = { x: snapped.x, y: Math.max(0, snapped.y), width: PLAN_CARD_DEFAULT_WIDTH, height: PLAN_CARD_DEFAULT_HEIGHT };
            placement = resolveFrozenAreaCandidate(candidate, api.getFrozenColumnWidth(), api.getFrozenRowHeight()) ?? candidate;
        }

        setEnablerCards((prev) => [...prev, newCard]);
        setPlacements((prev) => ({ ...prev, [id]: placement }));
        setIsEnablerModalOpen(false);
    }, []);

    /**
     * Deletes a standalone Enabler card outright — the one card kind that can be deleted from the
     * Plan board itself, since it has no source OST tree to delete it from instead. Only
     * `enablerCards` needs to change here: placements, connections, and planOrder all prune
     * themselves once the id disappears from `allItems` (see the pruning effects above), the same
     * way they already do when an OST node is deleted elsewhere. Only called via
     * handleRemoveEnablerCard below — never reachable for a tree-sourced Enabler card, which isn't
     * stored in `enablerCards` to begin with.
     */
    const handleDeleteEnablerCard = useCallback((itemId: string) => {
        setEnablerCards((prev) => prev.filter((card) => card.id !== itemId));
        setSelectedItemId((prev) => (prev === itemId ? null : prev));
    }, []);

    /**
     * PlanBoardCard's delete-X button handler for any Enabler card (see TimelineCanvas's
     * onDeleteItem prop) — branches on the card's origin: a tree-sourced Enabler card (an OST
     * enablerNode surfaced via usePlanBoardItems.ts, identifiable by its non-null treeName — see
     * EnablerCardItem's doc comment, types.ts) is sent back to the Plan column, same as dragging it
     * off the canvas; a standalone one (made via "Add enabler card") is deleted outright.
     */
    const handleRemoveEnablerCard = useCallback((itemId: string) => {
        const item = allItemsById.get(itemId);
        if (item && item.type === 'enabler' && item.treeName !== null) {
            sendItemBackToList(itemId);
        } else {
            handleDeleteEnablerCard(itemId);
        }
    }, [allItemsById, sendItemBackToList, handleDeleteEnablerCard]);

    /**
     * Handles every click on the Timeline's empty background — branches on whether a line tool
     * is currently armed (see linePlacementMode):
     *  - armed: resolves the click to a canvas point and places a new line there, clamped past
     *    the frozen row/column boundary (resolveLinePosition) so it can never spill into it.
     *  - not armed: falls back to the plain "clear the current selection" behavior a background
     *    click always had.
     */
    const handleTimelineBackgroundClick = useCallback((e: globalThis.MouseEvent) => {
        const api = timelineCanvasRef.current;
        if (linePlacementMode && api) {
            const point = api.screenToCanvas(e.clientX, e.clientY);
            if (point) {
                const rawPosition = linePlacementMode === 'vertical' ? point.x : point.y;
                const position = resolveLinePosition(linePlacementMode, rawPosition, api.getFrozenColumnWidth(), api.getFrozenRowHeight());
                const newLine: PlanDrawnLine = {
                    id: randomId(),
                    orientation: linePlacementMode,
                    position,
                    color: TIMELINE_DEFAULT_LINE_COLOR,
                    category: defaultCategoryForOrientation(linePlacementMode),
                };
                setLines((prev) => [...prev, newLine]);
            }
            setLinePlacementMode(null);
            return;
        }
        setSelectedItemId(null);
        setSelectedLineId(null);
        setLineColorPickerAnchor(null);
        setSelectedConnectionId(null);
    }, [linePlacementMode]);

    /**
     * mousedown on a rendered line does double duty, same threshold-gated click-vs-drag split
     * every other draggable element on this page uses (see beginCardDrag):
     *  - past DRAG_THRESHOLD_PX before release: moves the line, constrained to its own
     *    perpendicular axis (a vertical line only ever slides horizontally, and vice versa) and
     *    clamped past the frozen row/column boundary (resolveLinePosition) — recomputed from the
     *    line's ORIGINAL position and the total cursor delta every move, not accumulated frame to
     *    frame.
     *  - never crosses the threshold: a plain click — selects the line and anchors the popovers
     *    at the original mousedown point.
     */
    const handleLineMouseDown = useCallback((lineId: string, e: React.MouseEvent<SVGLineElement>) => {
        e.preventDefault();
        const api = timelineCanvasRef.current;
        const line = linesRef.current.find((l) => l.id === lineId);
        if (!api || !line) return;

        const startClientX = e.clientX;
        const startClientY = e.clientY;
        const zoom = api.getZoom();
        let dragged = false;

        const onMove = (mv: MouseEvent) => {
            const dx = mv.clientX - startClientX;
            const dy = mv.clientY - startClientY;
            if (!dragged) {
                if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
                dragged = true;
            }
            const deltaCanvas = (line.orientation === 'vertical' ? dx : dy) / zoom;
            const position = resolveLinePosition(line.orientation, line.position + deltaCanvas, api.getFrozenColumnWidth(), api.getFrozenRowHeight());
            setLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, position } : l)));
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            if (dragged) return;
            setSelectedItemId(null);
            setSelectedConnectionId(null);
            setSelectedLineId(lineId);
            setLineColorPickerAnchor({ left: startClientX, top: startClientY });
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, []);

    const handleLineColorChange = useCallback((lineId: string, color: string) => {
        setLines((prev) => prev.map((line) => (line.id === lineId ? { ...line, color } : line)));
    }, []);

    const handleLineNameChange = useCallback((lineId: string, name: string) => {
        setLines((prev) => prev.map((line) => (line.id === lineId ? { ...line, name } : line)));
    }, []);

    // Deleting a line also drops every axis label of its orientation — see
    // clearAxisLabelsForOrientation's doc comment (planAxisLabels.ts) for why a label can't
    // survive its line's deletion otherwise.
    const handleDeleteLine = useCallback((lineId: string) => {
        const deletedLine = linesRef.current.find((line) => line.id === lineId);
        setLines((prev) => prev.filter((line) => line.id !== lineId));
        if (deletedLine) {
            setAxisLabels((prev) => clearAxisLabelsForOrientation(prev, deletedLine.orientation));
        }
        setSelectedLineId(null);
        setLineColorPickerAnchor(null);
    }, []);

    const handleAxisLabelTextChange = useCallback((orientation: PlanLineOrientation, index: number, text: string) => {
        setAxisLabels((prev) => upsertAxisLabel(prev, orientation, index, { text }));
    }, []);

    // Removes a connection outright — shared by the relationship-type popover's delete button
    // and the Delete/Backspace keyboard shortcut below, so both paths stay in sync.
    const handleDeleteConnection = useCallback((connectionId: string) => {
        setConnections((prev) => prev.filter((c) => c.id !== connectionId));
        setSelectedConnectionId(null);
    }, []);

    // Ref on the shared popover group wrapper (color popover + options popover both live inside
    // it), not either popover individually — a click on either is still "inside" for this check.
    const lineColorPopoverRef = useRef<HTMLDivElement | null>(null);
    // Closes both popovers on any click outside the group — mirrors CircleMenu's own
    // outside-click pattern. The line's own SVG mousedown (handleLineMouseDown) already
    // stopPropagation()s, so re-clicking the same line to reposition the popovers doesn't
    // immediately close them via this listener first.
    useEffect(() => {
        if (!lineColorPickerAnchor) return;
        const onOutsideClick = (e: globalThis.MouseEvent) => {
            if (lineColorPopoverRef.current && !lineColorPopoverRef.current.contains(e.target as Node)) {
                setSelectedLineId(null);
                setLineColorPickerAnchor(null);
            }
        };
        window.addEventListener('mousedown', onOutsideClick);
        return () => window.removeEventListener('mousedown', onOutsideClick);
    }, [lineColorPickerAnchor]);

    // Same outside-click-closes pattern as the line popover above, for the connection
    // relationship-type popover. Only clears selectedConnectionId — connectionPopoverAnchor
    // clears itself via the effect declared alongside that state (see above). The connector
    // path's own mousedown (handleConnectorMouseDown, via GridCanvas) already stopPropagation()s,
    // so re-clicking the same connector to reposition the popover doesn't close it via this
    // listener first.
    const connectionPopoverRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!connectionPopoverAnchor) return;
        const onOutsideClick = (e: globalThis.MouseEvent) => {
            if (connectionPopoverRef.current && !connectionPopoverRef.current.contains(e.target as Node)) {
                setSelectedConnectionId(null);
            }
        };
        window.addEventListener('mousedown', onOutsideClick);
        return () => window.removeEventListener('mousedown', onOutsideClick);
    }, [connectionPopoverAnchor]);

    // Escape backs out of an armed line tool without placing anything, or dismisses the
    // popovers/selection if any is open — whichever is active takes priority. Delete/Backspace,
    // while a connection is selected, removes it — mirrors ProjectPage.tsx's own Delete/Backspace
    // node-deletion handler, including its input-focus guard (typing Backspace in, say, a card's
    // title or an axis-label cell must never delete a connection just because one happens to be
    // selected at the same time).
    useEffect(() => {
        if (!linePlacementMode && !selectedLineId && !selectedConnectionId) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (linePlacementMode) setLinePlacementMode(null);
                else if (selectedConnectionId) setSelectedConnectionId(null);
                else {
                    setSelectedLineId(null);
                    setLineColorPickerAnchor(null);
                }
                return;
            }

            if (!selectedConnectionId || !['Delete', 'Backspace'].includes(e.key)) return;
            const activeElement = document.activeElement as HTMLElement | null;
            if (activeElement && (['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName) || activeElement.isContentEditable)) {
                return;
            }
            e.preventDefault();
            handleDeleteConnection(selectedConnectionId);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [linePlacementMode, selectedLineId, selectedConnectionId, handleDeleteConnection]);

    // `active` marks the mode the canvas is currently waiting on a click for, which the old
    // CircleMenu could not show — its items were only visible while the menu was open, and it
    // closed on the very click that armed the mode.
    const TIMELINE_TOOLBAR_ACTIONS: PlanToolbarAction[] = useMemo(() => [
        {
            id: 'draw-vertical-line',
            icon: TIMELINE_MENU_VERTICAL_LINE_ICON,
            ariaLabel: 'Draw vertical line',
            active: linePlacementMode === 'vertical',
            onClick: () => setLinePlacementMode('vertical'),
        },
        {
            id: 'draw-horizontal-line',
            icon: TIMELINE_MENU_HORIZONTAL_LINE_ICON,
            ariaLabel: 'Draw horizontal line',
            active: linePlacementMode === 'horizontal',
            onClick: () => setLinePlacementMode('horizontal'),
        },
        {
            id: 'add-enabler-card',
            icon: TIMELINE_MENU_ADD_ENABLER_ICON,
            ariaLabel: 'Add enabler card',
            onClick: () => setIsEnablerModalOpen(true),
        },
    ], [linePlacementMode]);

    const hiddenId = activeDrag?.itemId ?? settleAnim?.itemId ?? null;
    // See resolveLivePlacements's own doc comment — passed to TimelineCanvas below instead of the
    // committed `placements` state directly, so a connector attached to the dragged card follows
    // it live.
    const livePlacements = resolveLivePlacements(placements, activeDrag);
    // While dragging a card that started in the list, its slot stays put (still rendered,
    // just shadow-lifted) instead of collapsing the moment the drag starts — collapsing it
    // immediately used to shift every other card up before the user had even decided where
    // to drop. The list's order/layout is only actually touched once the drag resolves: a
    // canvas drop removes it from `unplacedItems` once its cross-context settle commits (see
    // beginSettle/finishDrag), and a same-context list drop reorders `planOrder` directly at
    // that same moment — never mid-drag.
    const listElevatedItemId = pressedItemId ?? (activeDrag?.origin === 'list' ? activeDrag.itemId : null);
    // Past the drag threshold (ghost actually floating), not just pressed — dims the source
    // card to read as "lifted out", distinct from the plain shadow-lift shown on a mere press.
    // Ignored (see planDragSortState below) for the narrower case of a card still hovering the
    // very list it started in — that gets the Insights-panel-style live reorder preview instead.
    const listDraggingItemId = activeDrag?.origin === 'list' ? activeDrag.itemId : null;
    // Only show the list's own drop-placeholder when bringing a card IN from the canvas — since
    // `overList` is already scoped to the Plan list (see applyMove above), this is just the
    // canvas-origin/overList gate.
    const planDragOverInsertIndex = activeDrag && activeDrag.origin === 'canvas' && activeDrag.overList ? activeDrag.listInsertIndex : null;
    const planDragOverInsertHeight = activeDrag && activeDrag.origin === 'canvas' && activeDrag.overList ? activeDrag.listInsertHeight : null;

    // A card started in the Plan list AND is still hovering it — a plain same-list reorder, with
    // no canvas involved at all. This is the one drag shape this page's gesture and
    // InsightsPanel/TriagePanel's own drag-to-reorder gesture actually have in common, so it's
    // the only one given that same live "slide the other cards out of the way" preview (see
    // PlanBoardColumn's own dragSortState prop) — a card mid-flight toward (or back from) the
    // Timeline canvas keeps the pre-existing dimmed/placeholder treatment above, which has no
    // Insights-panel equivalent to match in the first place.
    const isSameListReorder = !!activeDrag && activeDrag.origin === 'list' && activeDrag.overList
        && activeDrag.listInsertIndex != null && activeDrag.listInsertHeight != null;
    const planFromIndex = isSameListReorder ? unplacedItems.findIndex((it) => it.id === activeDrag!.itemId) : -1;
    const planDragSortState = planFromIndex !== -1
        ? { fromIndex: planFromIndex, toIndex: activeDrag!.listInsertIndex as number, cardHeight: activeDrag!.listInsertHeight as number }
        : null;

    const ghostItemId = activeDrag?.itemId ?? settleAnim?.itemId ?? null;
    const ghostItem = ghostItemId ? allItemsById.get(ghostItemId) ?? null : null;
    const ghostRect: ScreenRect | null = activeDrag
        ? { left: activeDrag.ghostLeft, top: activeDrag.ghostTop, width: activeDrag.ghostWidth, height: activeDrag.ghostHeight }
        : settleAnim
            ? settleAnim.to
            : null;
    // clientWidth (not getBoundingClientRect().width) because `scrollbar-gutter: stable` on
    // .plan-column__list reserves extra space that only clientWidth accounts for — matching
    // this to how a real list card is actually sized keeps the probe's own measurements (and
    // the settle target derived from it) accurate. The first -2 -2 is the list's left+right
    // padding (see app.css); the -5 mirrors the card's own margin-right (see
    // .plan-board-card--list), which align-items:stretch subtracts from the real card's
    // rendered width.
    const probeListEl = planColumnListRef.current;
    const probeWidth = probeListEl ? probeListEl.clientWidth - 2 - 2 - 5 : undefined;
    const selectedLine = selectedLineId ? lines.find((line) => line.id === selectedLineId) ?? null : null;
    const selectedConnection = selectedConnectionId ? connections.find((c) => c.id === selectedConnectionId) ?? null : null;

    return (
        <AppPage>
            <div style={MAIN_CONTAINER_STYLE}>
                <div style={LEFT_COLUMN_STYLE}>
                    <AppPagePanel
                        title='Plan'
                        // minWidth: 0 overrides PagePanel's own default minWidth (280, also used
                        // as its flexBasis). Now the left column's only panel, so it fills the
                        // full height instead of sharing it with the Triage column below it
                        // (removed — see git history if that's ever needed again).
                        style={{ flex: '1 1 100%', minWidth: 0, minHeight: 0 }}
                    >
                        <PlanBoardColumn
                            items={unplacedItems}
                            emptyMessage='No stories or experiments yet.'
                            elevatedItemId={listElevatedItemId}
                            draggingItemId={listDraggingItemId}
                            selectedItemId={selectedItemId}
                            onBackgroundClick={() => setSelectedItemId(null)}
                            dragOverInsertIndex={planDragOverInsertIndex}
                            dragOverInsertHeight={planDragOverInsertHeight}
                            dragSortState={planDragSortState}
                            listRef={planColumnListRef}
                            onCardMouseDown={handleListCardMouseDown}
                        />
                    </AppPagePanel>
                </div>

                <div style={RIGHT_COLUMN_STYLE}>
                    <AppPagePanel
                        // `title` is a required AppPagePanel/PagePanel prop, but .plan-timeline-panel
                        // (app.css) hides the rendered title chip outright — the canvas is
                        // self-explanatory and the "TIMELINE" label was redundant chrome eating into
                        // the panel's already-precious space. TimelineCanvas paints its own full-bleed
                        // blueprint background/grid; .plan-timeline-panel also zeroes the shared
                        // page-panel title-bar padding so the canvas can actually reach every edge
                        // instead of leaving a border of the panel's own background around it. Now the
                        // right column's only panel (the Details placeholder below it was removed), so
                        // it fills the full height instead of sharing it 2/3-1/3.
                        title='Timeline'
                        className='plan-timeline-panel'
                        style={{ flex: '1 1 100%', minHeight: 0 }}
                    >
                        <TimelineCanvas
                            ref={timelineCanvasRef}
                            items={allItems}
                            placements={livePlacements}
                            hiddenPlacementId={hiddenId}
                            selectedItemId={selectedItemId}
                            onBackgroundClick={handleTimelineBackgroundClick}
                            onCardMouseDown={handleCanvasCardMouseDown}
                            onResizeHandleMouseDown={handleResizeHandleMouseDown}
                            dropForbidden={!!activeDrag && activeDrag.canvasDropRejected}
                            crosshair={!!linePlacementMode}
                            lines={lines}
                            selectedLineId={selectedLineId}
                            onLineMouseDown={handleLineMouseDown}
                            connectors={connectors}
                            connectorPreview={connectorPreview}
                            connectSourceItemId={connectDrag.sourceId}
                            connectTargetItemId={connectDrag.targetId}
                            onCardConnectHover={handleCardConnectHover}
                            onCardConnectLeave={handleCardConnectLeave}
                            onDeleteItem={handleRemoveEnablerCard}
                            modifierKeyActive={modifierKeyActive}
                            selectedConnectorId={selectedConnectionId}
                            onConnectorMouseDown={handleConnectorMouseDown}
                            axisLabels={axisLabels}
                            onAxisLabelTextChange={handleAxisLabelTextChange}
                            initialZoom={zoom}
                            onZoomChange={setZoom}
                            // One toolbar in the canvas's upper-right corner, pinned by its own
                            // CSS (.plan-timeline-toolbar); GridCanvas renders this slot inside
                            // the canvas wrapper but outside pan/zoom, so it overlaps the frozen
                            // row without belonging to it or scrolling with it.
                            cornerMenu={
                                <PlanTimelineToolbar actions={TIMELINE_TOOLBAR_ACTIONS}>
                                    <PlanSyncButton
                                        disabled={planSync.disabled}
                                        disabledReason={planSync.disabledReason}
                                        state={planSync.buttonState}
                                        onClick={planSync.runSync}
                                    />
                                </PlanTimelineToolbar>
                            }
                        />
                    </AppPagePanel>
                </div>
            </div>

            {ghostItem && (
                <div
                    ref={listProbeRef}
                    aria-hidden='true'
                    style={{ position: 'fixed', top: 0, left: -9999, visibility: 'hidden', pointerEvents: 'none', width: probeWidth }}
                >
                    <PlanBoardCard item={ghostItem} variant='list' />
                </div>
            )}

            {/* While bringing a card in from the canvas, the list's own drop-placeholder gap
                (planDragOverInsertIndex above) already marks the landing spot with the same
                dashed styling — showing the floating ghost too reads as two overlapping dashed
                boxes. Suppressed only in that exact case: an in-list reorder (origin 'list') has
                no such gap, so the ghost stays the only drag feedback there. */}
            {ghostItem && ghostRect && planDragOverInsertIndex == null && createPortal(
                <div
                    className='plan-board-card--ghost'
                    style={{
                        // Position is applied as a transform, NOT left/top: the ghost is
                        // pinned at the viewport origin by .plan-board-card--ghost (app.css)
                        // and translated from there. Moving via left/top relayouts and
                        // repaints the element on every mousemove, which would re-rasterize it
                        // each time. A transform is composited instead, so the placeholder is
                        // merely repositioned, never redrawn.
                        //
                        // Rounded to whole pixels so identical drag state always produces
                        // byte-identical CSS values — otherwise the raw floats from the pan/zoom
                        // math (e.g. 133.33333333333334px) can round very slightly differently
                        // between re-renders, landing the layer on a fractional offset.
                        transform: `translate3d(${Math.round(ghostRect.left)}px, ${Math.round(ghostRect.top)}px, 0)`,
                        width: Math.round(ghostRect.width),
                        height: Math.round(ghostRect.height),
                        transition: GHOST_RESIZE_TRANSITION,
                    }}
                />,
                document.body,
            )}

            {/* Popovers for the currently-selected drawn line, anchored near the click that
                selected/repositioned it — a color picker, and (directly below it, same width)
                an options panel: name, category, and delete. Fixed-position portal (like the
                drag ghost above) so both float above every panel regardless of where the line
                sits on the canvas. Both live inside one positioned group so the second stacks
                directly under the first with no separate anchor math to keep in sync. */}
            {selectedLine && lineColorPickerAnchor && createPortal(
                <div
                    ref={lineColorPopoverRef}
                    className='plan-line-popover-group'
                    style={{ left: lineColorPickerAnchor.left, top: lineColorPickerAnchor.top }}
                >
                    <div className='plan-line-color-popover'>
                        {LINE_COLOR_PRESETS.map((color) => (
                            <button
                                key={color}
                                type='button'
                                className='plan-line-color-popover__swatch'
                                style={{ backgroundColor: color }}
                                aria-label={`Set line color to ${color}`}
                                aria-pressed={selectedLine.color.toLowerCase() === color.toLowerCase()}
                                onClick={() => handleLineColorChange(selectedLine.id, color)}
                            />
                        ))}
                        <input
                            type='color'
                            className='plan-line-color-popover__custom'
                            value={selectedLine.color}
                            aria-label='Custom line color'
                            onChange={(e) => handleLineColorChange(selectedLine.id, e.target.value)}
                        />
                    </div>

                    <div className='plan-line-options-popover'>
                        {/* No category control here — a line's category is implied entirely by
                            its orientation (defaultCategoryForOrientation, set once at creation
                            time), so there's nothing left to edit besides the name and delete. */}
                        <input
                            type='text'
                            className='plan-line-options-popover__name'
                            placeholder='Line name'
                            aria-label='Line name'
                            value={selectedLine.name ?? ''}
                            onChange={(e) => handleLineNameChange(selectedLine.id, e.target.value)}
                        />
                        <button
                            type='button'
                            className='plan-line-options-popover__delete'
                            onClick={() => handleDeleteLine(selectedLine.id)}
                        >
                            Delete line
                        </button>
                    </div>
                </div>,
                document.body,
            )}

            {/* Relationship-type popover for the currently-selected connection, anchored above
                the point it was clicked (see handleConnectorMouseDown/connectionPopoverAnchor) —
                a fixed-position portal like the line popovers above, so it floats above every
                panel regardless of where the connector sits on the canvas. */}
            {selectedConnection && connectionPopoverAnchor && createPortal(
                <div
                    ref={connectionPopoverRef}
                    className='plan-connection-type-popover-anchor'
                    style={{ left: connectionPopoverAnchor.left, top: connectionPopoverAnchor.top }}
                >
                    <PlanConnectionRelationshipPopover
                        selectedType={selectedConnection.relationshipType ?? null}
                        onSelect={(type) => handleConnectionRelationshipTypeChange(selectedConnection.id, type)}
                        onDelete={() => handleDeleteConnection(selectedConnection.id)}
                    />
                </div>,
                document.body,
            )}

            {/* What the last Sync actually did. Shown for a plain success too, not just on
                failure: a run can partially succeed, or quietly fall back to body references
                when the repo has no sub-issue support, and both need to be visible. */}
            {(planSync.report || planSync.error) && (
                <SyncResultModal
                    report={planSync.report}
                    error={planSync.error}
                    onClose={planSync.dismissResult}
                />
            )}

            {/* Opened by the toolbar's "Add enabler card" button (see TIMELINE_TOOLBAR_ACTIONS) —
                see handleCreateEnablerCard for how the resulting card is placed. */}
            {isEnablerModalOpen && (
                <AddEnablerCardModal
                    onConfirm={handleCreateEnablerCard}
                    onClose={() => setIsEnablerModalOpen(false)}
                />
            )}
        </AppPage>
    );
}
